import { ApiError, IS_OFFLINE, readThrough, request, requestBody } from "./http";
import { AUTH_BASE_URL, IS_AUTH_MOCK, apiUrl, uploadUrl } from "./config";
import {
  ownerProfile,
  ownerListings,
  ownerOrders,
  ownerReviews,
  ownerMedia,
  ownerTrend,
  ownerDashboardStats,
  ownerDemoAccount,
} from "@/mocks/ownerData";
import { ownerLeads } from "@/mocks/ownerLeads";
import { adminDemoAccount, staffDemoAccount } from "@/mocks/adminData";
import type {
  AuthUser,
  OwnedListing,
  OwnerDashboardStats,
  OwnerLead,
  OwnerMedia,
  OwnerOrder,
  OwnerTrendPoint,
} from "@/types";

export const TOKEN_KEY = "myyp_owner_token";
export const USER_KEY = "myyp_owner_user";
export const REFRESH_KEY = "myyp_owner_refresh";

export { ownerDemoAccount as DEMO_ACCOUNT };

export interface RegisterPayload {
  name: string;
  email: string;
  phone: string;
  company: string;
  password: string;
}

export interface OwnerReview {
  id: string;
  businessId: string;
  businessName: string;
  authorName: string;
  rating: number;
  comment: string;
  date: string;
  replied: boolean;
}

export interface OwnerDashboardData {
  stats: OwnerDashboardStats;
  trend: OwnerTrendPoint[];
}

interface RawActor {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  city?: string;
  avatar?: string;
  plan?: string;
  memberSince?: string;
  role?: "owner" | "admin" | "staff";
}

function toAuthUser(raw: RawActor, fallback: AuthUser): AuthUser {
  return {
    id: raw.id ?? fallback.id,
    name: raw.name ?? fallback.name,
    email: raw.email ?? fallback.email,
    phone: raw.phone ?? fallback.phone,
    company: raw.company ?? fallback.company,
    city: raw.city ?? fallback.city,
    avatar: raw.avatar ?? fallback.avatar,
    plan: raw.plan ?? fallback.plan,
    memberSince: raw.memberSince ?? fallback.memberSince,
    role: raw.role ?? fallback.role,
  };
}

const DEMO_USER: AuthUser = toAuthUser(ownerProfile, {
  id: "usr-owner-1",
  name: "Owner",
  email: ownerDemoAccount.email,
  phone: "",
  company: "",
  city: "Mysore",
  avatar: "",
  plan: "Free",
  memberSince: "2026-01-01",
  role: "owner",
});

function simulate<T>(value: T, ms = 320): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/* ------------------------- Auth ------------------------- */

/** Extra top-level keys the PHP auth endpoints emit alongside `data`. */
interface RawSession {
  token?: string;
  refreshToken?: string;
  refresh_token?: string;
  expiresIn?: number;
  refreshExpiresIn?: number;
  user?: RawActor;
  data?: {
    token?: string;
    refreshToken?: string;
    refresh_token?: string;
    expiresIn?: number;
    refreshExpiresIn?: number;
    user?: RawActor;
  };
}

export interface AuthSession {
  token: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  user: AuthUser;
}

const AUTH_REQUEST = { baseUrl: AUTH_BASE_URL } as const;

function normalizeSession(body: RawSession | null): AuthSession {
  const data = body?.data ?? {};
  const token = body?.token || data.token || "";
  const refreshToken = body?.refreshToken || body?.refresh_token || data.refreshToken || data.refresh_token || "";
  const expiresIn = body?.expiresIn ?? data.expiresIn ?? 3600;
  const refreshExpiresIn = body?.refreshExpiresIn ?? data.refreshExpiresIn ?? 0;
  if (!token) {
    throw new ApiError("The server did not return a session. Please try again.", 401, "InvalidCredentials");
  }
  return {
    token,
    refreshToken,
    expiresIn,
    refreshExpiresIn,
    user: toAuthUser(body?.user ?? data.user ?? {}, DEMO_USER),
  };
}

export async function authLogin(email: string, password: string): Promise<AuthSession> {
  if (IS_AUTH_MOCK) {
    const target = email.trim().toLowerCase();
    const demo = [
      { ...ownerDemoAccount, role: "owner" as const },
      { ...adminDemoAccount, role: "admin" as const },
      { ...staffDemoAccount, role: "staff" as const },
    ].find((a) => a.email.toLowerCase() === target && a.password === password);

    if (demo) {
      return simulate<AuthSession>({
        token: "demo-token",
        refreshToken: "demo-refresh-token",
        expiresIn: 3600,
        refreshExpiresIn: 2592000,
        user: { ...DEMO_USER, name: demo.name || DEMO_USER.name, email: demo.email, role: demo.role },
      });
    }
    throw new ApiError("Incorrect email or password. Try the demo account below.", 401, "InvalidCredentials");
  }

  const body = await requestBody<RawSession>("/auth/login", {
    method: "POST",
    body: { email, password },
    ...AUTH_REQUEST,
  });
  return normalizeSession(body);
}

export async function authRegister(payload: RegisterPayload): Promise<AuthSession> {
  if (IS_AUTH_MOCK) {
    const user: AuthUser = {
      ...DEMO_USER,
      id: `usr-${Date.now()}`,
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      company: payload.company,
      plan: "Free",
      memberSince: new Date().toISOString().slice(0, 10),
    };
    return simulate<AuthSession>({
      token: "demo-token",
      refreshToken: "demo-refresh-token",
      expiresIn: 3600,
      refreshExpiresIn: 2592000,
      user,
    });
  }

  const body = await requestBody<RawSession>("/auth/register", {
    method: "POST",
    body: payload,
    ...AUTH_REQUEST,
  });
  return normalizeSession(body);
}

export async function authRefresh(refreshToken: string): Promise<AuthSession> {
  const body = await requestBody<RawSession>("/auth/refresh", {
    method: "POST",
    body: { refresh_token: refreshToken },
    ...AUTH_REQUEST,
  });
  return normalizeSession(body);
}

export async function authLogout(refreshToken: string | null): Promise<void> {
  if (IS_AUTH_MOCK || !refreshToken) return;
  try {
    await request("/auth/logout", {
      method: "POST",
      body: { refresh_token: refreshToken },
      ...AUTH_REQUEST,
    });
  } catch {
    // Best-effort — the client session is cleared regardless.
  }
}

export async function authMe(token: string): Promise<AuthUser> {
  const raw = await request<RawActor>("/auth/me", { token, ...AUTH_REQUEST });
  return toAuthUser(raw, DEMO_USER);
}

/* ------------------------- Owner data ------------------------- */

export const getOwnerDashboard = (token: string | null): Promise<OwnerDashboardData> =>
  readThrough<OwnerDashboardData>("/owner/dashboard", { stats: ownerDashboardStats, trend: ownerTrend }, { token });

export const getOwnerListings = (token: string | null): Promise<OwnedListing[]> =>
  readThrough<OwnedListing[]>("/owner/businesses", ownerListings as OwnedListing[], { token });

export const getOwnerListingById = (id: string, token: string | null): Promise<OwnedListing | undefined> =>
  readThrough<OwnedListing | undefined>(
    `/owner/businesses/${id}`,
    (ownerListings as OwnedListing[]).find((l) => l.id === id),
    { token },
  );

export const getOwnerOrders = (token: string | null): Promise<OwnerOrder[]> =>
  readThrough<OwnerOrder[]>("/owner/orders", ownerOrders as OwnerOrder[], { token });

export const getOwnerReviews = (token: string | null): Promise<OwnerReview[]> =>
  readThrough<OwnerReview[]>("/owner/reviews", ownerReviews as OwnerReview[], { token });

export const getOwnerMedia = (token: string | null): Promise<OwnerMedia[]> =>
  readThrough<OwnerMedia[]>("/owner/media", ownerMedia as OwnerMedia[], { token });

/**
 * Uploads a listing photo to the server's /uploads folder and returns its
 * public URL (resolved through the same upload base as branding images).
 * Throws on failure so the media page can surface a clear message — never
 * falls back to a browser object URL, so nothing is stored in browser temp.
 */
export async function uploadOwnerMedia(file: File, token: string | null): Promise<string> {
  if (IS_OFFLINE) {
    throw new ApiError("Photo uploads are available on the live site.", 0, "Offline");
  }

  const form = new FormData();
  form.append("file", file);

  const res = await fetch(apiUrl("/owner/upload"), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });

  const raw = await res.text();
  let parsed: { code?: string; data?: { url?: string }; meta?: { message?: string } } | null = null;
  try {
    parsed = raw ? (JSON.parse(raw) as typeof parsed) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok || (parsed?.code && parsed.code !== "OK")) {
    throw new ApiError(parsed?.meta?.message || "Upload failed. Please try another image.", res.status);
  }
  const url = parsed?.data?.url;
  if (!url) throw new ApiError("The server did not return an image URL.", 500);
  return uploadUrl(url);
}

/* ---------------- Leads (§14) ---------------- */

/** Enquiries and contact events against the owner's listings. */
export const getOwnerLeads = (token: string | null): Promise<OwnerLead[]> =>
  readThrough<OwnerLead[]>(
    "/owner/leads",
    ownerLeads.map((l) => ({
      ...l,
      source: l.source as OwnerLead["source"],
      status: l.status as OwnerLead["status"],
    })),
    { token },
  );

/** Persists a lead's workflow status. Resolves to success (never throws). */
export async function updateOwnerLeadStatus(
  id: string,
  status: OwnerLead["status"],
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) return simulate(true);
  try {
    await request("/owner/leads", { method: "POST", token, body: { id, status } });
    return true;
  } catch {
    return false;
  }
}

export const getOwnerProfile = (token: string | null): Promise<AuthUser> =>
  readThrough<AuthUser>("/owner/profile", DEMO_USER, { token });

/** Create (id === "new") or update an owner listing. Resolves to success. */
export async function saveOwnerListing(
  id: string,
  payload: Partial<OwnedListing>,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) return simulate(true);
  try {
    await request(`/owner/businesses/${id}`, { method: "PUT", token, body: payload });
    return true;
  } catch {
    return false;
  }
}

export async function updateOwnerProfile(payload: Partial<AuthUser>, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) return simulate(true);
  try {
    await request("/owner/profile", { method: "PUT", token, body: payload });
    return true;
  } catch {
    return false;
  }
}

/** Persist an owner reply to a review. Resolves to success. */
export async function replyToOwnerReview(
  reviewId: string,
  reply: string,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) return simulate(true);
  try {
    await request("/owner/reviews", { method: "POST", token, body: { id: reviewId, action: "reply", reply } });
    return true;
  } catch {
    return false;
  }
}

/** Removes one of the owner's own media photos. Resolves to success. */
export async function deleteOwnerMedia(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) return simulate(true);
  try {
    await request(`/owner/media/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/** Permanently removes one of the owner's own listings. Resolves to success. */
export async function deleteOwnerListing(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) return simulate(true);
  try {
    await request(`/owner/businesses/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}